import { lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { type Environment, fetchQuery, graphql, RelayEnvironmentProvider } from 'react-relay';
import serverVars from 'server-vars';

import { IntlProvider } from 'dibs-react-intl';
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense.tsx';
import { mobile } from 'dibs-client-check';
import { getBuyerId } from 'dibs-cookie-jar';

import { isAppDownloadOnCooldown } from './appDownloadStorage.ts';
import { APP_DOWNLOAD_MODAL_TYPES, DAYS_SINCE_USED_APP } from './constants.ts';

import {
    type loadAppDownloadItemSavedModalQuery,
    type loadAppDownloadItemSavedModalQuery$data,
} from './__generated__/loadAppDownloadItemSavedModalQuery.graphql.ts';

const { appDownloadModalItemSaved } = APP_DOWNLOAD_MODAL_TYPES;

const AppDownloadItemSavedModalLazy = lazy(() =>
    import(
        /* webpackChunkName: "AppDownloadItemSavedModal" */ './AppDownloadItemSavedModal.tsx'
    ).then(mod => ({
        default: mod.AppDownloadItemSavedModal,
    }))
);

async function loadAppDownloadItemSavedModal(relayEnvironment: Environment): Promise<void> {
    if (isAppDownloadOnCooldown(appDownloadModalItemSaved)) {
        return;
    }

    const isMobile = serverVars.get('settings.isMobile');
    const isIphone = mobile.iPhone();

    if (!isMobile || !isIphone) {
        return;
    }

    const userId = getBuyerId(document.cookie);

    if (!userId) {
        return;
    }

    let data: loadAppDownloadItemSavedModalQuery$data | null | undefined = null;

    try {
        data = await fetchQuery<loadAppDownloadItemSavedModalQuery>(
            relayEnvironment,
            graphql`
                query loadAppDownloadItemSavedModalQuery(
                    $userId: String!
                    $daysSinceUsedApp: Int!
                ) {
                    viewer {
                        user(userId: $userId) {
                            hasUsedAppInLastDays(days: $daysSinceUsedApp)
                        }
                    }
                }
            `,
            { userId, daysSinceUsedApp: DAYS_SINCE_USED_APP }
        ).toPromise();
    } catch {
        return;
    }

    const hasUsedAppInLastDays = data?.viewer?.user?.hasUsedAppInLastDays;

    if (hasUsedAppInLastDays) {
        return;
    }

    const locale = serverVars.get('locale');
    const messages = serverVars.get('messages');

    const getWrapperElement = (): HTMLDivElement => {
        const wrapperElement = document.createElement('div');
        document.body.appendChild(wrapperElement);
        return wrapperElement;
    };
    const root = createRoot(getWrapperElement());

    root.render(
        <IntlProvider locale={locale} messages={messages}>
            <ClientSuspense fallback="">
                <RelayEnvironmentProvider environment={relayEnvironment}>
                    <AppDownloadItemSavedModalLazy mobileFullScreen={isMobile} />
                </RelayEnvironmentProvider>
            </ClientSuspense>
        </IntlProvider>
    );
}

export { loadAppDownloadItemSavedModal, appDownloadModalItemSaved };
